"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// Deep imports (not the "@obby/components" barrel) so we pull ONLY the popup +
// its Stitches config — the barrel drags in Next.js-only components
// (TeamMember → next/image, Stories → next/dynamic) the WC build can't resolve.
import {
  WaitingListPopup,
  useWaitingListPopup,
  type WaitingListRequest,
} from "@obby/components/WaitingListPopup";
import { getCssText } from "@obby/components/sitches.config";
import { loadAndApplyThemeOverrides } from "~/widgets/lib/theme";
import { openCheckout } from "~/widgets/lib/checkout-loader";
import { WAITING_LIST_PORTAL_ID } from "@baluu/ui/lib/checkout/portals";

// Same CSS all widgets inject into their shadow root (tailwind + theme vars).
// @ts-expect-error - Vite inline import
import cssText from "~/widgets/styles/globals.css?inline";

export type WaitingListPortalProps = {
  isOpen: boolean;
  listingId: string;
  /** The sold-out session being joined. Enables the "this date / any date"
   *  choice and the "other dates" cross-sell. Omit for a course-wide join. */
  sessionId?: string;
  /** When present, joins a TERM's waiting list (Phase I.5) — the popup offers
   *  "This term" / "This date" (drop-in) / "Any term". */
  eventId?: string;
  apiUrl?: string;
  primaryColor?: string;
  /** Business slug for loading theme overrides. */
  businessSlug?: string;
  onClose: () => void;
  onSuccess?: () => void;
};

/**
 * The embeddable waiting-list join popup. Renders the SHARED
 * `@obby/components` WaitingListPopup (the same one the widget uses) and shares
 * its data/submit logic via `useWaitingListPopup`, so the two surfaces can't
 * drift. Kept webcomponents-specific: the portal + own shadow root (embed
 * isolation), theme overrides, the fetch transport, the `baluu:waiting-list-
 * joined` event, and the cross-sell navigation.
 *
 * Styling: WaitingListPopup styles via Stitches, which injects into
 * `document.head` — blocked by the shadow boundary. So we mirror `getCssText()`
 * into a `<style>` inside this shadow root (`:root`→`:host` for its theme vars),
 * alongside the tailwind bundle.
 */
export function WaitingListPortal({
  isOpen,
  listingId,
  sessionId,
  eventId,
  apiUrl = "https://api.baluu.io",
  primaryColor = "#03c2b9",
  businessSlug,
  onClose,
  onSuccess
}: WaitingListPortalProps) {
  const [shadowContainer, setShadowContainer] = useState<HTMLDivElement | null>(
    null
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const stitchesStyleRef = useRef<HTMLStyleElement | null>(null);

  // Transport for the shared hook: the webcomponents hit ${apiUrl}/widget/* via
  // fetch. Memoized on apiUrl so the hook's fetch effect stays stable.
  const request = useCallback<WaitingListRequest>(
    async (method, path, body) => {
      const res = await fetch(`${apiUrl}/widget/${path}`, {
        method,
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined
      });
      if (!res.ok) {
        let message = "Failed to join waiting list";
        try {
          const b = await res.json();
          if (b?.message) message = b.message;
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }
      return method === "GET" ? res.json() : res;
    },
    [apiUrl]
  );

  const {
    data,
    isLoading,
    onSubmit: submitJoin
  } = useWaitingListPopup({ listingId, sessionId, eventId, request, enabled: isOpen });

  // Create the body-level portal container + its shadow root when opened.
  useEffect(() => {
    if (isOpen && !containerRef.current) {
      const container = document.createElement("div");
      container.id = WAITING_LIST_PORTAL_ID;
      document.body.appendChild(container);
      containerRef.current = container;

      const shadowRoot = container.attachShadow({ mode: "open" });

      // Widget CSS (tailwind + theme vars); :root -> :host so vars cascade.
      let css =
        cssText ||
        (window as unknown as { __BALUU_WIDGETS_CSS__?: string })
          .__BALUU_WIDGETS_CSS__ ||
        "";
      if (css) {
        css = css.replace(/:root\s*\{/g, ":host {");
        const style = document.createElement("style");
        style.textContent = css;
        shadowRoot.appendChild(style);
      }

      // Full-screen overlay host positioning.
      const overlayStyle = document.createElement("style");
      overlayStyle.textContent = `:host { position: fixed; inset: 0; z-index: 999999; display: block; }`;
      shadowRoot.appendChild(overlayStyle);

      // Stitches sheet — kept in sync by the effect below as the popup renders.
      const stitchesStyle = document.createElement("style");
      stitchesStyle.setAttribute("data-stitches", "");
      shadowRoot.appendChild(stitchesStyle);
      stitchesStyleRef.current = stitchesStyle;

      const shadowContainerEl = document.createElement("div");
      shadowContainerEl.id = "shadow-content";
      shadowRoot.appendChild(shadowContainerEl);
      setShadowContainer(shadowContainerEl);

      if (businessSlug) {
        loadAndApplyThemeOverrides(businessSlug, shadowRoot, apiUrl).catch(err =>
          console.warn("[WaitingListPortal] Failed to load theme overrides:", err)
        );
      }
      document.body.style.overflow = "hidden";
    }

    return () => {
      if (!isOpen && containerRef.current) {
        containerRef.current.remove();
        containerRef.current = null;
        stitchesStyleRef.current = null;
        setShadowContainer(null);
        document.body.style.overflow = "";
      }
    };
  }, [isOpen, businessSlug, apiUrl]);

  // Keep the Stitches CSS mirrored into the shadow root. getCssText() is
  // cumulative and the popup's styles are generated during its render, so a
  // plain (dep-less) effect after each commit captures them; :root -> :host for
  // the Stitches theme variables.
  useEffect(() => {
    if (stitchesStyleRef.current) {
      stitchesStyleRef.current.textContent = getCssText().replace(
        /:root\s*\{/g,
        ":host {"
      );
    }
  });

  // Escape to close.
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        containerRef.current.remove();
        document.body.style.overflow = "";
      }
    };
  }, []);

  // The shared hook does the join POST + list routing; the webcomponents add
  // the success event + onSuccess callback on top.
  async function onSubmit(params: WaitingListPopup.SubmitParams) {
    const res = await submitJoin(params);
    document.dispatchEvent(
      new CustomEvent("baluu:waiting-list-joined", {
        bubbles: true,
        composed: true,
        detail: {
          listingId,
          eventId:
            params.target === "term" ? data.term?.eventId ?? eventId : null,
          sessionId:
            params.target === "session" ? params.sessionId ?? sessionId : null,
          target: params.target,
          email: params.email
        }
      })
    );
    onSuccess?.();
    return res;
  }

  // Picking an alternative date opens the checkout pre-selected on it — the
  // webcomponents equivalent of the widget's book-now?session= navigation.
  function onSelectAlternativeDate(altSessionId: string) {
    const schoolId = data.course?.schoolId;
    onClose();
    if (schoolId) {
      void openCheckout({
        listingId,
        businessId: schoolId,
        apiUrl,
        primaryColor,
        initialSessionId: altSessionId
      });
    } else {
      document.dispatchEvent(
        new CustomEvent("baluu:waiting-list-alternative-date", {
          bubbles: true,
          composed: true,
          detail: { listingId, sessionId: altSessionId }
        })
      );
    }
  }

  // Picking a similar experience: let the host page navigate (embeds have no
  // router). Include the courseId if we can resolve it from the slug.
  function onSelectSimilarExperience(slug: string) {
    const match = data.similarExperiences?.find(e => e.slug === slug);
    document.dispatchEvent(
      new CustomEvent("baluu:waiting-list-similar-experience", {
        bubbles: true,
        composed: true,
        detail: { slug, courseId: match?.courseId ?? null }
      })
    );
    onClose();
  }

  if (!isOpen || !shadowContainer) return null;

  return createPortal(
    !isLoading && data.course ? (
      <WaitingListPopup
        course={data.course}
        session={data.session ?? null}
        location={data.location}
        canJoinAnyDate={data.canJoinAnyDate}
        alternativeDates={data.alternativeDates}
        onSelectAlternativeDate={onSelectAlternativeDate}
        similarExperiences={data.similarExperiences}
        onSelectSimilarExperience={onSelectSimilarExperience}
        term={data.term}
        hasDropInEnabled={data.hasDropInEnabled}
        eligible={data.eligible}
        bookableNow={data.bookableNow}
        availableDates={data.availableDates}
        selectedSessionId={sessionId}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    ) : null,
    shadowContainer
  );
}
